import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AddressService implements OnDestroy {

  private permissionState = new BehaviorSubject<PermissionState | null>(null);
  readonly locationPermission$ = this.permissionState.asObservable();
  private enablePermissionDialog = new Subject<void>();
  readonly enablePermissionDialog$ = this.enablePermissionDialog.asObservable();
  private locationPermissionRestored = new Subject<void>();
  readonly locationPermissionRestored$ = this.locationPermissionRestored.asObservable();
  private permissionStatus: PermissionStatus;
  private destroyed = false;
  private permissionObservationInitialized = false;
  private automaticLocationRequest: Promise<{ longitude: number, latitude: number } | null> | null = null;
  private gpsLocationRequest: Promise<{ longitude: number, latitude: number }> | null = null;
  private lastGpsLocation: { longitude: number, latitude: number, capturedAt: number } | null = null;
  private readonly recentGpsMaxAgeMs = 15000;
  private addressDialogOpen = false;
  private automaticLocationCancellationVersion = 0;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone
  ) { }

  initializeLocationPermission(): Promise<{ longitude: number, latitude: number } | null> {
    if (this.addressDialogOpen) {
      return Promise.resolve(null);
    }

    if (this.automaticLocationRequest) {
      return this.automaticLocationRequest;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve(null);
    }

    if (!window.isSecureContext) {
      this.updatePermission('denied');
      return Promise.resolve(null);
    }

    if (!navigator.geolocation) {
      return Promise.resolve(null);
    }

    if (!this.permissionObservationInitialized) {
      this.permissionObservationInitialized = true;
      void this.observeLocationPermission();
    }

    const cancellationVersion = this.automaticLocationCancellationVersion;
    const locationRequest = this.getUserGpsLocation()
      .then(location => {
        if (this.addressDialogOpen || cancellationVersion !== this.automaticLocationCancellationVersion) {
          return null;
        }
        return location;
      })
      .catch(() => {
        // Location access is optional and must not block app initialization.
        return null;
      });
    this.automaticLocationRequest = locationRequest;

    void locationRequest.then(() => {
      // Keep only an in-flight request. A later eligible page should get fresh coordinates.
      if (this.automaticLocationRequest === locationRequest) {
        this.automaticLocationRequest = null;
      }
    });

    return locationRequest;
  }

  setAddressDialogOpen(isOpen: boolean): void {
    if (isOpen && !this.addressDialogOpen) {
      // Invalidate an automatic location request that began before manual editing.
      this.automaticLocationCancellationVersion++;
    }
    this.addressDialogOpen = isOpen;
  }

  isAddressDialogOpen(): boolean {
    return this.addressDialogOpen;
  }

  getAutomaticLocationCancellationVersion(): number {
    return this.automaticLocationCancellationVersion;
  }

  canApplyAutomaticLocation(cancellationVersion: number): boolean {
    return !this.addressDialogOpen
      && cancellationVersion === this.automaticLocationCancellationVersion;
  }

  private async observeLocationPermission(): Promise<void> {
    try {
      if (!navigator.permissions?.query) {
        return;
      }

      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (this.destroyed) {
        return;
      }

      this.permissionStatus = status;
      // A request may finish before this query; preserve its actual result.
      if (this.permissionState.value === null) {
        this.updatePermission(status.state);
      }
      status.onchange = () => {
        const previousState = this.permissionState.value;
        this.updatePermission(status.state);

        // Chrome's "Allow for this site" only changes permission; retry location separately.
        if (previousState === 'denied' && status.state === 'granted' && !this.destroyed) {
          this.zone.run(() => this.locationPermissionRestored.next());
        }
      };
    } catch {
      // Older browsers use the geolocation callbacks below instead.
    }
  }

  private updatePermission(state: PermissionState): void {
    if (!this.destroyed) {
      this.zone.run(() => this.permissionState.next(state));
    }
  }

  showEnablePermissionDialog(): void {
    this.zone.run(() => this.enablePermissionDialog.next());
  }

  getUserGpsLocation(useRecentLocation: boolean = false): Promise<{ longitude: number, latitude: number }> {
    if (useRecentLocation && this.lastGpsLocation
      && Date.now() - this.lastGpsLocation.capturedAt <= this.recentGpsMaxAgeMs) {
      return Promise.resolve({
        longitude: this.lastGpsLocation.longitude,
        latitude: this.lastGpsLocation.latitude
      });
    }

    if (this.gpsLocationRequest) {
      return this.gpsLocationRequest;
    }

    const locationRequest = new Promise<{ longitude: number, latitude: number }>((resolve, reject) => {
      if (isPlatformBrowser(this.platformId) && navigator.geolocation) {
        // Call directly so a click-triggered request retains the user's activation.
        navigator.geolocation.getCurrentPosition((resp) => {
          this.updatePermission('granted');
          const location = { longitude: resp.coords.longitude, latitude: resp.coords.latitude };
          this.lastGpsLocation = { ...location, capturedAt: Date.now() };
          resolve(location);
        }, (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            // A blocked/dismissed request can fail while the query still says "prompt".
            this.updatePermission('denied');
          }
          reject(err);
        }, {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: useRecentLocation ? this.recentGpsMaxAgeMs : 0
        });
      } else {
        reject(new Error('Geolocation is not available.'));
      }
    });

    this.gpsLocationRequest = locationRequest;
    void locationRequest.then(
      () => {
        if (this.gpsLocationRequest === locationRequest) {
          this.gpsLocationRequest = null;
        }
      },
      () => {
        if (this.gpsLocationRequest === locationRequest) {
          this.gpsLocationRequest = null;
        }
      }
    );

    return locationRequest;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.permissionStatus) {
      this.permissionStatus.onchange = null;
    }
    this.permissionState.complete();
    this.enablePermissionDialog.complete();
    this.locationPermissionRestored.complete();
  }
}
